import type { DropdownUser } from '@/api/capa/types';
import type {
  CollaborationStatus,
  ProductCategory,
  RelationshipType,
  StyleTypePage,
} from '@/api/dropdowns/types';
import type { FacilityProcessGroup } from '@/api/orders/types';
import type { DropdownCompany } from '@/api/product-mapping/types';
import { API_ROOT_URL, BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

export async function fetchCollaborationStatuses(): Promise<{
  data: CollaborationStatus[];
}> {
  return apiFetch<{ data: CollaborationStatus[] }>(
    '/api/companies/collaboration-statuses',
    {},
    API_ROOT_URL,
  );
}

export async function fetchRelationshipTypes(): Promise<{
  data: RelationshipType[];
}> {
  return apiFetch<{ data: RelationshipType[] }>(
    '/api/companies/relationship-types',
    {},
    API_ROOT_URL,
  );
}

export async function fetchFacilityProcessOptions(): Promise<{
  data: FacilityProcessGroup[];
}> {
  return apiFetch<{ data: FacilityProcessGroup[] }>(
    '/api/facility-processes/dropdown',
    {},
    API_ROOT_URL,
  );
}

export async function fetchCoworkerOptions(): Promise<{
  data: DropdownUser[];
}> {
  return apiFetch<{ data: DropdownUser[] }>(
    '/api/users?isDropDown=true&selectCoWorkers=true',
    {},
    API_ROOT_URL,
  );
}

export async function fetchUsersByCompanyIds(
  companyIds: string,
): Promise<DropdownUser[]> {
  return apiFetch<DropdownUser[]>(
    `/api/user-companies/list-by-company-ids?companyIds=${encodeURIComponent(companyIds)}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchNetworkCompaniesDropdown(): Promise<{
  data: DropdownCompany[];
}> {
  return apiFetch<{ data: DropdownCompany[] }>(
    '/api/companies?isDropDown=true&isSupplierNetwork=true',
    {},
    API_ROOT_URL,
  );
}

/** One page of the companies dropdown. Unlike the two calls above this keeps
 *  the `meta.totalCount` envelope, so a server-paginated, searched picker
 *  (`usePaginatedSelect`) can page through a network too large to fetch whole. */
export async function fetchCompaniesDropdownPage(qs: string): Promise<{
  data: DropdownCompany[];
  meta: { totalCount: number };
}> {
  return apiFetch<{ data: DropdownCompany[]; meta: { totalCount: number } }>(
    `/api/companies?${qs}`,
    {},
    API_ROOT_URL,
  );
}

export async function fetchAllCompaniesDropdown(): Promise<{
  data: DropdownCompany[];
}> {
  return apiFetch<{ data: DropdownCompany[] }>(
    '/api/companies?isDropDown=true&includeOwnCompany=true',
    {},
    API_ROOT_URL,
  );
}

export async function fetchProductCategories(): Promise<{
  data: ProductCategory[];
}> {
  return apiFetch<{ data: ProductCategory[] }>(
    '/api/product-categories',
    {},
    API_ROOT_URL,
  );
}

export async function fetchStyleTypeListPage(
  body: object,
): Promise<StyleTypePage> {
  return apiFetch<StyleTypePage>(
    '/style-types/list',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export interface ParentOptionPage {
  data: Array<{ id: string; name: string; styleId?: string | null }>;
  metadata?: { pagination?: { total?: number } };
}

export async function fetchParentStyleOptions(
  body: object,
): Promise<ParentOptionPage> {
  return apiFetch<ParentOptionPage>(
    '/styles/list',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export async function fetchParentVariantOptions(
  body: object,
): Promise<ParentOptionPage> {
  return apiFetch<ParentOptionPage>(
    '/style-variants/list',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export async function fetchRawMaterials(): Promise<{
  data: Array<{ id: string; name: string }>;
}> {
  return apiFetch<{ data: Array<{ id: string; name: string }> }>(
    '/api/materials?isDropDown=true',
    {},
    API_ROOT_URL,
  );
}
